import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  AfterViewChecked,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimeoutError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ChatbotService } from './chatbot.service';
import { AiAssistantConversation, ChatbotMessage } from './chatbot.types';

type ChatbotState = 'idle' | 'loading' | 'ready' | 'unavailable';

interface PendingSend {
  message: string;
  idempotencyKey: string;
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  standalone: false,
})
export class ChatbotComponent implements OnInit, AfterViewChecked {
  @ViewChild('messageContainer') messageContainer!: ElementRef;

  isOpen = false;
  isTyping = false;
  isResetting = false;
  state: ChatbotState = 'idle';

  messages: ChatbotMessage[] = [];
  messageText = '';
  dir = 'ltr';

  // Last failed send, kept so "Retry" reuses the same idempotency key
  private failedSend: PendingSend | null = null;
  private shouldScrollToBottom = false;
  private destroyRef = inject(DestroyRef);

  constructor(
    private chatbotService: ChatbotService,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.dir = localStorage.getItem('html-dir') || 'ltr';
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  get canSend() {
    return this.state === 'ready' && !this.isTyping && !this.isResetting;
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    if (
      this.isOpen &&
      (this.state === 'idle' || this.state === 'unavailable')
    ) {
      this.loadConversation();
    }
  }

  loadConversation() {
    this.state = 'loading';
    this.chatbotService
      .initConversation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.onConversationLoaded(res),
        error: () => (this.state = 'unavailable'),
      });
  }

  sendMessage() {
    const text = this.messageText.trim();
    if (!text || !this.canSend) return;

    this.messages.push({
      id: 'user-' + Date.now(),
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    });
    this.messageText = '';
    this.send({ message: text, idempotencyKey: this.generateKey() });
  }

  retryLastMessage() {
    if (!this.failedSend || !this.canSend) return;
    this.messages = this.messages.filter((m) => !m.canRetry);
    this.send(this.failedSend);
  }

  startNewConversation() {
    if (this.isResetting || this.isTyping) return;
    this.isResetting = true;
    this.chatbotService
      .resetConversation()
      .pipe(
        switchMap(() => this.chatbotService.initConversation()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.isResetting = false;
          this.onConversationLoaded(res);
        },
        error: (err: HttpErrorResponse) => {
          this.isResetting = false;
          if (err?.status === 403) {
            this.state = 'unavailable';
            return;
          }
          this.pushError(this.errorMessageKey(err), false);
        },
      });
  }

  private onConversationLoaded(res: AiAssistantConversation) {
    if (!res?.isEnabled || !res?.isEligible) {
      this.state = 'unavailable';
      return;
    }
    this.state = 'ready';
    this.failedSend = null;
    this.addWelcomeMessage();
  }

  private send(pending: PendingSend) {
    this.isTyping = true;
    this.failedSend = null;
    this.shouldScrollToBottom = true;

    this.chatbotService
      .sendMessage({
        message: pending.message,
        idempotencyKey: pending.idempotencyKey,
        correlationId: `chat-${Date.now()}`,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.messages.push({
            id: 'bot-' + Date.now(),
            role: 'assistant',
            content: response.reply,
            createdAt: new Date().toISOString(),
          });
          this.isTyping = false;
          this.shouldScrollToBottom = true;
        },
        error: (err: HttpErrorResponse | TimeoutError) => {
          this.isTyping = false;
          if (err instanceof HttpErrorResponse && err.status === 403) {
            this.state = 'unavailable';
            return;
          }
          const canRetry = this.isRetryable(err);
          if (canRetry) this.failedSend = pending;
          this.pushError(this.errorMessageKey(err), canRetry);
        },
      });
  }

  private isRetryable(err: HttpErrorResponse | TimeoutError) {
    if (err instanceof TimeoutError) return true;
    return [0, 502, 503, 504].includes(err?.status);
  }

  private errorMessageKey(err: HttpErrorResponse | TimeoutError) {
    if (err instanceof TimeoutError) return 'CHATBOT.TIMEOUT_ERROR';
    switch (err?.status) {
      case 400:
        return 'CHATBOT.VALIDATION_ERROR';
      case 404:
        return 'CHATBOT.NOT_FOUND_ERROR';
      case 502:
      case 503:
      case 504:
        return 'CHATBOT.TIMEOUT_ERROR';
      default:
        return 'CHATBOT.ERROR_MESSAGE';
    }
  }

  private pushError(key: string, canRetry: boolean) {
    this.messages.push({
      id: 'err-' + Date.now(),
      role: 'assistant',
      content: this.translateService.instant(key),
      createdAt: new Date().toISOString(),
      isError: true,
      canRetry,
    });
    this.shouldScrollToBottom = true;
  }

  private generateKey(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  private addWelcomeMessage() {
    this.messages = [
      {
        id: 'welcome',
        role: 'assistant',
        content: this.translateService.instant('CHATBOT.WELCOME_MESSAGE'),
        createdAt: new Date().toISOString(),
      },
    ];
    this.shouldScrollToBottom = true;
  }

  private scrollToBottom() {
    try {
      const el = this.messageContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }
}
